<template>
    <app-layout>
        <div class="flex flex-col">
            <page-header :title="trans('tasks.overview.header.title')" title-in-app-header>
                <div class="flex flex-col gap-3">
                    <div class="filterbar items-center-lg flex flex-col justify-between gap-4 xs:flex-row">
                        <filter-bar v-model="queryParams" :config="filterBarConfig">
                            <template #locations-option="{ option }">
                                <div class="flex items-center justify-between gap-3">
                                    <span>{{ option.label }}</span>
                                    <small class="text-muted">{{ option.iataCode }}</small>
                                </div>
                            </template>
                            <template #taskTypes-option="{ option }">
                                <div class="flex items-center justify-between">
                                    <span class="mr-3">{{ option.label }}</span>
                                    <em :class="getTaskGroupColor(option.value)" class="block h-4 w-4 rounded-sm border-1 border-white" />
                                </div>
                            </template>
                            <template #assetTypes-option="{ option }">
                                <div class="flex items-center justify-between">
                                    <span class="mr-3">{{ option.label }}</span>
                                    <small class="text-muted">{{ option.shortLabel }}</small>
                                </div>
                            </template>
                        </filter-bar>
                        <div class="flex flex-grow items-end justify-end gap-2">
                            <div class="flex items-center justify-between gap-2">
                                <label class="form-label m-0 !text-base">{{ trans('general.grouped_by') }}:</label>
                                <dropdown
                                    :caret="false"
                                    class="w-auto border-0 !bg-transparent"
                                    menu-classes="p-2 border-2 !border-primary"
                                    toggler-classes="!rounded-xl border-2 justify-content-start"
                                >
                                    <template #toggler>
                                        <div class="flex items-center gap-2 text-left text-nowrap text-muted">
                                            {{
                                                groupByOptions.find((option) => option.value === (queryParams?.form.groupBy || groupBy)).label || '-'
                                            }}
                                            <chevron-down />
                                        </div>
                                    </template>
                                    <div style="max-height: 200px; overflow: auto">
                                        <dropdown-item v-for="option in groupByOptions" :key="option.value" @click="updateGroupBy(option.value)">
                                            {{ option.label }}
                                        </dropdown-item>
                                    </div>
                                </dropdown>
                            </div>
                        </div>
                    </div>
                    <asset-type-toggle-buttons
                        v-if="queryParams && assetTypesOptions.length > 1"
                        :model-value="queryParams?.form?.assetTypes || []"
                        :options="assetTypesOptions"
                        class="hidden md:flex"
                        @update:model-value="updateAssetTypes"
                    />
                </div>
            </page-header>
            <start-here-widget class="mb-6" @filter-by-asset="filterByAsset" />

            <div class="flex flex-col gap-12">
                <template v-if="isFixedGrouping">
                    <template v-for="section in currentSections" :key="section.id">
                        <task-section
                            v-if="sectionShouldShow(section.id)"
                            :id="section.id"
                            :collapsed="collapsedSections.includes(section.id)"
                            :count="section.count"
                            :default-sort="section.defaultSort"
                            :empty-text="trans('tasks.overview.section.today.empty')"
                            :fields="getSectionFields(section)"
                            :loading="!sectionLoaded(section.id) && !collapsedSections.includes(section.id)"
                            :prop-name="section.id"
                            :show-work-order-column="section.id === 'approvedTasks'"
                            :tasks="sectionTasks(section.id)"
                            :title="section.title"
                            @update:collapsed="toggleCollapsed"
                        />
                    </template>
                    <div v-if="!hasVisibleFixedSections" class="p-4">{{ trans('tasks.overview.empty') }}</div>
                </template>

                <!-- Other groupings (airport / specific_location / area): one
                     InfiniteScroll per dynamically-discovered location. The
                     order list lands first via `locationGroupOrder`, then each
                     location's items stream through its own `locationGroup_<id>`
                     ScrollProp with an independent cursor URL parameter. -->
                <Deferred v-else data="locationGroupOrder">
                    <template #fallback>
                        <task-section id="loading" :loading="true" />
                    </template>
                    <div v-if="!locationGroupOrder.length" class="p-4">{{ trans('tasks.overview.empty') }}</div>
                    <template v-for="group in locationGroupOrder" :key="group.id">
                        <task-section
                            v-if="group.count !== 0"
                            :id="group.id"
                            :collapsed="collapsedSections.includes(group.id)"
                            :count="group.count"
                            :default-sort="{ key: 'status', direction: 'asc' }"
                            :empty-text="trans('tasks.overview.section.today.empty')"
                            :fields="getSectionFields(group)"
                            :loading="!sectionLoaded(locationGroupPropName(group.id)) && !collapsedSections.includes(group.id)"
                            :prop-name="locationGroupPropName(group.id)"
                            :tasks="locationGroupTasks(group.id)"
                            :title="group.label"
                            @update:collapsed="toggleCollapsed"
                        />
                    </template>
                </Deferred>
            </div>
        </div>
    </app-layout>
</template>

<script setup>
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Components/PageHeader.vue';
import { Deferred, router, usePage } from '@inertiajs/vue3';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { trans } from '@/mixins/i18n';
import TaskSection from './TaskSection.vue';
import StartHereWidget from './StartHereWidget.vue';
import FilterBar from '@/Components/FilterBar.vue';
import Dropdown from '@/Components/Dropdown.vue';
import DropdownItem from '@/Components/DropdownItem.vue';
import { getTaskGroupColor } from '@/Pages/TaskList/utils.js';
import ChevronDown from '../../../icons/ChevronDown.vue';
import AssetTypeToggleButtons from '@/Components/AssetTypeToggleButtons.vue';
import { eventBus, events } from '@/eventBus.js';
import { useSessionStorage } from '@/composables/useSessionStorage';

const page = usePage();
const sessionStorage = useSessionStorage();
const collapsedSections = ref([]);
const queryParams = ref(null);
const collapseInitialized = ref(false);

const groupBy = computed(() => page.props.filters.groupBy);

// Section identifiers (`openTasks`, `todayTasks`, ...) are used uniformly as
// the HTML id, Inertia prop name, and groupCounts key — so every section just
// carries its id and translated title.
const TIME_SECTIONS = [
    { id: 'todayTasks', title: trans('tasks.overview.section.today.title') },
    { id: 'upcomingTasks', title: trans('tasks.overview.section.upcoming.title') },
    { id: 'pastTasks', title: trans('tasks.overview.section.past.title') },
];

const STATUS_SECTION_DEFS = {
    openTasks: { id: 'openTasks', title: trans('tasks.overview.section.open.title') },
    pendingApprovalTasks: { id: 'pendingApprovalTasks', title: trans('tasks.overview.section.pending_approval.title') },
    approvedTasks: { id: 'approvedTasks', title: trans('tasks.overview.section.approved.title') },
};
const statusSections = computed(() => {
    const order = page.props.statusSectionOrder ?? Object.keys(STATUS_SECTION_DEFS);
    return order.map((id) => STATUS_SECTION_DEFS[id]).filter(Boolean);
});

const counts = computed(() => page.props.groupCounts ?? {});

const isFixedGrouping = computed(() => groupBy.value === 'time' || groupBy.value === 'status');
const hasVisibleFixedSections = computed(() => currentSections.value.some((s) => sectionShouldShow(s.id)));

// Single unified shape for the time/status sections so the template renders
// them through one v-for. Location grouping has its own dynamic source.
const currentSections = computed(() => {
    const base = groupBy.value === 'status' ? statusSections.value : TIME_SECTIONS;
    return base.map((s) => ({
        ...s,
        count: counts.value[s.id],
        defaultSort: { key: 'status', direction: s.id === 'pastTasks' ? 'desc' : 'asc' },
    }));
});

const sectionLoaded = (id) => page.props[id]?.data !== undefined;
const sectionTasks = (id) => page.props[id]?.data ?? [];

// Decides whether a time/status section's wrapper should render at all.
// When loaded, hide it if the result set is empty. When not loaded (typically
// a collapsed section that we never fetched), fall back to the count from
// `groupCounts`: a confirmed zero hides the section; an unknown/null count
// keeps it visible so the collapsed header still appears while counts load.
const sectionShouldShow = (id) => {
    if (sectionLoaded(id)) {
        return sectionTasks(id).length > 0;
    }
    return counts.value[id] !== 0;
};

// Section data props are LazyScrollProps on the backend — they are not part
// of the initial page response and never auto-fetched. The client must
// explicitly request them via `router.reload({ only })` when a section is
// visible. `inflight` dedupes concurrent reload calls per section.
const inflight = new Set();

const loadSection = (id, propName) => {
    if (inflight.has(id) || sectionLoaded(propName)) {
        return;
    }
    inflight.add(id);
    router.reload({
        only: [propName],
        preserveScroll: true,
        onFinish: () => inflight.delete(id),
    });
};

// Section ids double as prop names for time/status grouping; location
// grouping uses the prefixed `locationGroup_<id>` form.
const sectionsForCurrentGrouping = () => {
    if (groupBy.value === 'status') {
        return statusSections.value.map((s) => ({ id: s.id, propName: s.id }));
    }
    if (groupBy.value === 'time') {
        return TIME_SECTIONS.map((s) => ({ id: s.id, propName: s.id }));
    }
    return locationGroupOrder.value.map((g) => ({ id: g.id, propName: locationGroupPropName(g.id) }));
};

const loadVisibleSections = () => {
    const sections = sectionsForCurrentGrouping().filter(
        (s) => !collapsedSections.value.includes(s.id) && !sectionLoaded(s.propName) && !inflight.has(s.id),
    );
    if (sections.length === 0) {
        return;
    }
    sections.forEach((s) => inflight.add(s.id));
    router.reload({
        only: sections.map((s) => s.propName),
        preserveScroll: true,
        onFinish: () => sections.forEach((s) => inflight.delete(s.id)),
    });
};

// Location-grouped views surface a dynamic set of sections — one per
// discovered ancestor (airport/specific_location/area). `locationGroupOrder`
// is the order+labels prop; each section's items live on its own
// `locationGroup_<id>` ScrollProp so cursors stay isolated per location.
const locationGroupPropName = (id) => `locationGroup_${id}`;
// locationGroupOrder is emitted as a bare array (Inertia::defer wraps the
// closure result directly — no JsonResource `.data` envelope).
const locationGroupOrder = computed(() => page.props.locationGroupOrder ?? []);
const locationGroupTasks = (id) => page.props[locationGroupPropName(id)]?.data ?? [];

const collapsedSectionsStorageKey = computed(() => `tasks.overview.collapsedSections.${groupBy.value ?? 'default'}`);

const restoreCollapsedSections = () => {
    const stored = sessionStorage.getItem(collapsedSectionsStorageKey.value);
    if (stored === null) {
        return false;
    }
    try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
            collapsedSections.value = parsed;
            return true;
        }
    } catch {
        // fall through to defaults
    }
    return false;
};

const persistCollapsedSections = () => {
    sessionStorage.setItem(collapsedSectionsStorageKey.value, JSON.stringify(collapsedSections.value));
};

// Collapse defaults are applied once per page mount, not on every partial reload.
// Pagination loads (InfiniteScroll merging) re-trigger the groups watcher; without
// this guard they'd repeatedly collapse the sections the user just expanded.
const collapseSectionsIfNecessary = () => {
    if (collapseInitialized.value) {
        return;
    }
    collapseInitialized.value = true;
    if (restoreCollapsedSections()) {
        return;
    }
    collapsedSections.value = [];
    if (!isFixedGrouping.value && locationGroupOrder.value.length > 1) {
        collapsedSections.value = locationGroupOrder.value.map((g) => g.id);
    }
};

const toggleCollapsed = ({ id, collapsed }) => {
    if (collapsed) {
        collapsedSections.value.push(id);
    } else {
        collapsedSections.value = collapsedSections.value.filter((section) => section !== id);
        const propName = sectionsForCurrentGrouping().find((s) => s.id === id)?.propName;
        if (propName) {
            loadSection(id, propName);
        }
    }
    persistCollapsedSections();
};

const groupByOptions = computed(() => page.props.filters?.groupings?.data ?? []);
const getGroupDateColumnTitle = (group) => {
    if (groupBy.value === 'time') {
        if (group.id === 'pastTasks') {
            return trans('tasks.overview.item.status.title');
        }
        return trans('tasks.overview.item.due_at.title');
    }
    return trans('tasks.overview.item.date.title');
};

const getSectionFields = (group) => {
    const fields = [
        { name: trans('tasks.overview.item.task.title') },
        { name: getGroupDateColumnTitle(group), sortBy: 'status' },
        { name: trans('tasks.overview.item.title.title') },
        { name: trans('tasks.overview.item.location.title'), sortBy: 'location' },
        { name: trans('tasks.overview.item.asset.title'), sortBy: 'asset' },
    ];

    if (group.id === 'approvedTasks') {
        fields.push({ name: trans('tasks.overview.item.work_order.title') });
    }

    return fields;
};

const locationsOptions = computed(() =>
    (page.props.locations?.data || []).map((loc) => ({
        label: loc.name,
        value: loc.icaoCode,
        iataCode: loc.iataCode,
        searchableContent: `${loc.iataCode}|${loc.icaoCode}|${loc.name}`,
    })),
);
const specificLocationsOptions = computed(() => (page.props.specificLocations?.data || []).map((loc) => ({ label: loc.name, value: loc.id })));
// Returns null while the deferred prop has never loaded (→ show skeleton),
// the cached value while a re-navigation is in flight (→ no flicker),
// or the live value once the response arrives.
const useStickyOptions = (getter) => {
    const cache = ref(undefined); // undefined = never loaded
    watch(
        getter,
        (v) => {
            if (v !== null) {
                cache.value = v;
            }
        },
        { immediate: true },
    );
    return computed(() => {
        const current = getter();
        if (current !== null) return current;
        if (cache.value !== undefined) return cache.value;
        return null;
    });
};

const areasOptions = useStickyOptions(() => (page.props.areas != null ? (page.props.areas.data ?? []) : null));
const assetTypesOptions = useStickyOptions(() => (page.props.assetTypes != null ? (page.props.assetTypes.data ?? []) : null));
const taskTypesOptions = useStickyOptions(() => (page.props.taskTypes != null ? (page.props.taskTypes.data ?? []) : null));

const filterBarConfig = {
    route: route('tasks.index'),
    // Filter options are `get`s so Vue reads the latest computed value each
    // render — needed because taskTypes/assetTypes/areas arrive as deferred
    // Inertia props after the initial response.
    filters: [
        { type: 'search', key: 'search', placeholder: trans('tasks.overview.filter.search') },
        {
            type: 'multi-select',
            key: 'locations',
            label: trans('dashboard.filter.locations'),
            get options() {
                return locationsOptions.value;
            },
            showSearchbar: true,
        },
        {
            type: 'multi-select',
            key: 'specificLocations',
            label: trans('activities.overview.cell.specific_location'),
            get options() {
                return specificLocationsOptions.value;
            },
            showSearchbar: true,
        },
        {
            type: 'multi-select',
            key: 'areas',
            label: trans('activities.overview.cell.areas'),
            get options() {
                return areasOptions.value;
            },
            showSearchbar: true,
        },
        {
            type: 'multi-select',
            key: 'taskTypes',
            label: trans('tasks.overview.filter.task_type'),
            get options() {
                return taskTypesOptions.value;
            },
        },
        {
            type: 'multi-select',
            key: 'assetTypes',
            label: trans('tasks.overview.filter.asset_type'),
            get options() {
                return assetTypesOptions.value;
            },
            class: 'md:!hidden',
        },
    ],
    onLoadSuccess: () => {
        collapseInitialized.value = false;
        collapseSectionsIfNecessary();
        loadVisibleSections();
    },
    additionalData: () => ({
        groupBy: queryParams.value.form.groupBy,
    }),
};

const updateAssetTypes = (value) => {
    if (!queryParams?.value?.form) return;
    queryParams.value.form.assetTypes = value;
    queryParams.value.change();
};

const filterByAsset = (curieId) => {
    if (!queryParams.value?.form) return;
    queryParams.value.form.reset();
    queryParams.value.form.search = curieId;
    queryParams.value.change();
};

const updateGroupBy = (value) => {
    if (!queryParams?.value?.form) return;
    queryParams.value.form.groupBy = value;
    queryParams.value.change();
};

watch(locationGroupOrder, () => {
    collapseSectionsIfNecessary();
    loadVisibleSections();
});

const reloadGroups = () => {
    // Refresh only sections that have already been loaded.
    // Keep never-opened sections lazy so unrelated actions
    // don't trigger unnecessary queries.
    const loadedSectionProps = sectionsForCurrentGrouping()
        .filter((section) => sectionLoaded(section.propName))
        .map((section) => section.propName);

    const structuralProp = isFixedGrouping.value ? 'groupCounts' : 'locationGroupOrder';

    const url = new URL(window.location.href);

    url.searchParams.forEach((_, key) => {
        if (key.startsWith('cursor_')) {
            url.searchParams.delete(key);
        }
    });

    router.visit(url.toString(), {
        only: [structuralProp, ...loadedSectionProps],
        reset: loadedSectionProps,
    });
};

// taskApproved and workOrderCreated are commonly emitted together — an
// approval that also creates a work order fires both within the same tick.
// Each emit is queued via setTimeout(0) by the event bus, so a microtask
// guard wouldn't coalesce them; schedule the reload via setTimeout and clear
// any pending one when a second event arrives in the same tick.
let pendingReload = null;
const scheduleReload = () => {
    if (pendingReload !== null) {
        return;
    }
    pendingReload = setTimeout(() => {
        pendingReload = null;
        reloadGroups();
    }, 0);
};

// Restore persisted collapsed state synchronously during setup, before the
// first render — otherwise collapsed sections briefly render as expanded
// with a loading skeleton, then snap shut once `onMounted` runs. We only
// restore here (no defaults): for location grouping `groups` is still empty
// at this point because the order prop is deferred, and the watcher below
// applies the "collapse-all-by-default" rule once it arrives.
if (restoreCollapsedSections()) {
    collapseInitialized.value = true;
}

onMounted(() => {
    eventBus.$on(events.taskApproved, scheduleReload);
    eventBus.$on(events.workOrderCreated, scheduleReload);
    loadVisibleSections();
});

onUnmounted(() => {
    eventBus.$off(events.taskApproved, scheduleReload);
    eventBus.$off(events.workOrderCreated, scheduleReload);
    if (pendingReload !== null) {
        clearTimeout(pendingReload);
        pendingReload = null;
    }
});
</script>
